import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  TableEmpty,
} from '@/components/ui/Table';
import { EyeIcon, KeyIcon, RefreshIcon, ShieldIcon, SyncIcon, TrashIcon } from '@/components/icons';
import type { CertificateActions } from '@/config/resourceRules';
import type { Certificate } from '@/types/certificate';
import { CertificateStatusIndicator, ExpirationIndicator } from './CertificateStatus';

/**
 * Row actions of the certificates table
 */
export type CertificateRowAction = keyof CertificateActions;

export interface CertificatesTableProps {
  certificates: Certificate[];
  isLoading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  /** Shown in the empty state when set (user may issue certificates) */
  onIssueFirst?: () => void;
  getDomainName: (domainId: string) => string;
  getNodeName: (nodeId: string | null) => string;
  /** Which actions are available for a row (permissions + state) */
  getActions: (cert: Certificate) => CertificateActions;
  /** Whether an action is in progress for a row */
  isBusy: (action: CertificateRowAction, certId: string) => boolean;
  onAction: (action: CertificateRowAction, cert: Certificate) => void;
}

const COLUMN_COUNT = 6;

/**
 * Certificates table with status/expiry indicators and row actions
 */
export function CertificatesTable({
  certificates,
  isLoading,
  hasActiveFilters,
  onClearFilters,
  onIssueFirst,
  getDomainName,
  getNodeName,
  getActions,
  isBusy,
  onAction,
}: CertificatesTableProps) {
  return (
    <Table isLoading={isLoading} skeletonColumns={COLUMN_COUNT} skeletonRows={5}>
      <TableHead>
        <TableRow>
          <TableHeader>Domain / Common Name</TableHeader>
          <TableHeader>Node</TableHeader>
          <TableHeader>Issuer</TableHeader>
          <TableHeader>Expires</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader align="right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {certificates.length === 0 && !isLoading ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <TableEmpty
                icon={<ShieldIcon />}
                message={
                  hasActiveFilters ? 'No certificates match your filters' : 'No certificates issued yet'
                }
                action={
                  hasActiveFilters ? (
                    <Button variant="secondary" size="sm" onClick={onClearFilters}>
                      Clear filters
                    </Button>
                  ) : (
                    onIssueFirst && (
                      <Button variant="secondary" size="sm" onClick={onIssueFirst}>
                        Issue your first certificate
                      </Button>
                    )
                  )
                }
              />
            </TableCell>
          </TableRow>
        ) : (
          certificates.map((cert) => {
            const actions = getActions(cert);

            return (
              <TableRow key={cert.id}>
                <TableCell>
                  <div className="certs-name-cell">
                    <div className="certs-domain-row">
                      <span className="certs-domain">{getDomainName(cert.domain_id)}</span>
                      {cert.has_content && (
                        <span
                          className="certs-stored-badge"
                          title="Certificate and private key stored on control plane"
                        >
                          <KeyIcon />
                        </span>
                      )}
                    </div>
                    <span className="certs-common-name">{cert.common_name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="certs-node">{getNodeName(cert.node_id)}</span>
                </TableCell>
                <TableCell>
                  <span className="certs-issuer">{cert.issuer || '-'}</span>
                </TableCell>
                <TableCell>
                  <ExpirationIndicator expiresAt={cert.expires_at} />
                </TableCell>
                <TableCell>
                  <CertificateStatusIndicator status={cert.status} expiresAt={cert.expires_at} />
                </TableCell>
                <TableCell align="right">
                  <div className="certs-actions">
                    {actions.view && (
                      <IconButton
                        className="certs-action-btn certs-action-btn-view"
                        icon={<EyeIcon />}
                        label={`View certificate for ${cert.common_name}`}
                        title="View certificate details"
                        onClick={() => onAction('view', cert)}
                      />
                    )}
                    {actions.sync && (
                      <IconButton
                        className="certs-action-btn certs-action-btn-sync"
                        icon={<SyncIcon />}
                        label={`Sync certificate for ${cert.common_name}`}
                        title="Sync certificate content from node"
                        isBusy={isBusy('sync', cert.id)}
                        onClick={() => onAction('sync', cert)}
                      />
                    )}
                    {actions.renew && (
                      <IconButton
                        className="certs-action-btn"
                        icon={<RefreshIcon />}
                        label={`Renew certificate for ${cert.common_name}`}
                        title="Renew certificate"
                        isBusy={isBusy('renew', cert.id)}
                        onClick={() => onAction('renew', cert)}
                      />
                    )}
                    {actions.retry && (
                      <IconButton
                        className="certs-action-btn certs-action-btn-retry"
                        icon={<RefreshIcon />}
                        label={`Retry certificate for ${cert.common_name}`}
                        title="Retry certificate issuance"
                        isBusy={isBusy('retry', cert.id)}
                        onClick={() => onAction('retry', cert)}
                      />
                    )}
                    {actions.delete && (
                      <IconButton
                        className="certs-action-btn certs-action-btn-danger"
                        icon={<TrashIcon />}
                        label={`Delete certificate for ${cert.common_name}`}
                        title="Delete certificate"
                        onClick={() => onAction('delete', cert)}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
